import { isDefined, isNotDefined } from '@sgme/fp';
import { Button, Tooltip } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { format } from 'date-fns';
import type { ReactElement } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { useRecordPriceMutation } from '@/api/mifid';
import {
  useForwardAccumulatorSelector,
  useForwardAccumulatorTileId,
} from '@/App/tiles/ForwardAccumulator/ProductProvider.tsx';
import { getForwardAccumulatorQuote, getForwardAccumulatorRfsId } from '@/store/forwardAccumulator/selectors.ts';
import { FULL_DATE_FORMAT_TEXT } from '@/utils/date.ts';

export function TimestampButton(): ReactElement {
  const tileId = useForwardAccumulatorTileId();
  const [recordPrice] = useRecordPriceMutation();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { mifid, rfsStatus, quoteId, rfsId, quoteCcy } = useForwardAccumulatorSelector((accumulator) => {
    const quote = getForwardAccumulatorQuote(accumulator);
    const currencyIndex = accumulator.properties.premiumTypeCurrencyIndex;

    return {
      rfsId: getForwardAccumulatorRfsId(accumulator),
      quoteId: quote?.quoteId,
      quoteCcy: currencyIndex === 1 ? quote?.premiumData.premiumCcy1 : quote?.premiumData.premiumCcy2,
      mifid: accumulator.properties.mifid,
      rfsStatus: accumulator.properties.rfs?.status,
    };
  });

  const hasBeenUsed = isDefined(mifid?.records?.length) ? mifid.records.length > 0 : false;
  const isDisabled = rfsStatus !== 'PRICING';

  function handleRecordPrice() {
    if (isNotDefined(rfsId) || isNotDefined(quoteCcy) || isNotDefined(quoteId)) {
      return;
    }

    recordPrice({
      tileId,
      date: Date.now(),
      quoteId,
      rfsId,
      prices: [
        {
          bid: 0,
          ask: Number.isNaN(Number(quoteCcy)) ? 0 : Number(quoteCcy),
        },
      ],
    });
    sgwtWebAnalytics?.trackEvent(
      'Forward Accumulator',
      'Timestamp button clicked',
      isDefined(rfsId) ? rfsId : 'unknown',
    );
  }

  return (
    <div className="d-flex gap-2">
      <Tooltip contents={<FormattedMessage id="accumulator.footer.timestampBtn.tooltip" />}>
        <Button
          size="sm"
          purpose="primary"
          color={isDisabled || hasBeenUsed ? 'primary' : 'danger'}
          iconPosition="start"
          disabled={isDisabled}
          onClick={handleRecordPrice}
          data-e2e={'button-timestamp'}
        >
          <i className="icon icon-sm">timer</i>
          <FormattedMessage id="accumulator.footer.timestampBtn" />
        </Button>
      </Tooltip>
      {!isDisabled && <PriceRecordHistory />}
    </div>
  );
}

function PriceRecordHistory() {
  const { currencyPair, mifid } = useForwardAccumulatorSelector((accumulator) => ({
    currencyPair: accumulator.properties.currencyPair,
    mifid: accumulator.properties.mifid || { records: [] },
  }));

  return (
    <OverlayTrigger
      rootClose
      placement="bottom"
      trigger="click"
      overlay={
        <Popover style={{ maxWidth: '300px' }}>
          <Popover.Header data-e2e="timestamp-popover-title">
            <div className="d-flex flex-column">
              <FormattedMessage
                tagName="div"
                id="priceRecordsHistory.popover.title"
                values={{ currencyPair, instrument: 'Accumulator' }}
              />
              <FormattedMessage
                tagName="div"
                id="priceRecordsHistory.popover.subtitle"
                values={{
                  date: isDefined(mifid.rfsDate) ? format(mifid.rfsDate, FULL_DATE_FORMAT_TEXT) : '',
                }}
              />
            </div>
          </Popover.Header>

          <Popover.Body>
            <div className="d-flex flex-column gap-1" data-e2e="timestamp-popover-body">
              {mifid.records.toReversed().map((record, index) => (
                <div key={record.date}>
                  {record.isError ? (
                    <div className="text-danger">
                      <i className="icon icon-sm">error</i>
                      <FormattedMessage
                        id="priceRecordsHistory.popover.item.error"
                        values={{
                          item: mifid.records.length - index,
                          date: format(record.date, FULL_DATE_FORMAT_TEXT),
                        }}
                      />
                    </div>
                  ) : (
                    <FormattedMessage
                      id="priceRecordsHistory.popover.item"
                      values={{
                        item: mifid.records.length - index,
                        date: format(record.date, FULL_DATE_FORMAT_TEXT),
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </Popover.Body>
        </Popover>
      }
    >
      {mifid.records.some((r) => r.isError) ? (
        <div className="border badge rounded-pill badge-discreet-secondary fs-12 cursor-pointer text-danger">
          <i className="icon icon-sm">error</i>
          <FormattedMessage id="priceRecordsHistory.recording.error" />
        </div>
      ) : (
        <div
          data-e2e="timestamp-count"
          className="border badge rounded-pill badge-discreet-secondary fs-12 cursor-pointer"
        >
          {mifid.records?.length ?? 0}
        </div>
      )}
    </OverlayTrigger>
  );
}
